import { useContext, forwardRef } from "react";
import type { DeepMap, FieldError, FormState } from "react-hook-form";
import type { InputActionMeta, MenuPlacement } from "react-select";
import Select, { components } from "react-select";
import { ThemeContext } from "styled-components/macro";
import type { OptionType } from "../../types/types";
import { showErrors } from "../../util/util-components";
import {
  AutofilledIndicator,
  CustomOption,
  CustomOptionWithStyledParens,
  CustomSingleValueWithStyledParens,
  CustomValueContainer,
  DropdownIndicator,
  Menu,
} from "../SelectBoxShared/SelectBoxShared";
import { useTranslation } from "react-i18next";
import { hasValue } from "./SelectBoxV2.utils";
import { filterOption } from "../../util/util";
import { borderPrimitiveTokens } from "../../designTokens";

const getDropdownIndicator = (isAutofilled: boolean, isSearchBar: boolean) => {
  if (isAutofilled) {
    return AutofilledIndicator;
  }
  if (isSearchBar) {
    return DropdownIndicator;
  }
  return components.DropdownIndicator;
};

/* 
  This cannot be called by itself, only as a controlled input inside of a <form>
  managed by react-hook-form.  See CreateNewOffer for a working example.

          <Controller
            as={SelectBoxV2}
            control={control}
            name="product"
            placeholder="Product Name"
            options={products}
            rules={{ required: true }}
            errors={errors}
            formState={formState}
          />
*/

export interface SelectBoxProps {
  formState: FormState<Record<string, any>>;
  errors: DeepMap<Record<string, any>, FieldError>;
  name: string;
  options: OptionType[];
  placeholder: string;
  defaultValue?: OptionType;
  isSearchBar?: boolean;
  menuPlacement?: MenuPlacement;
  onInputChange?:
    | ((newValue: string, actionMeta: InputActionMeta) => void)
    | undefined;
  setDefaultTerm?: boolean;
  normalPlaceholder?: boolean;
  isCreatable?: boolean;
  isClearable?: boolean;
  disabled?: boolean;
  isSearchable?: boolean;
  [x: string]: any;
  error?: FieldError;
  minHeight?: number;
  height?: number;
  valueContainerMT?: string;
  enableIndicatorsContainer?: boolean;
  useStyledOption?: boolean;
  isAutofilled?: boolean;
}

/**
 * usage:
 * 
 *                    <Controller
                      placeholder={"SKU"}
                      name={"sku"}
                      as={SelectBoxV2}
                      control={control}
                      options={skuOptions}
                      rules={{ required: true }}
                      errors={errors}
                      formState={formState}
                      defaultValue={skuOptions[0]}
                      isDisabled={true}
                    />
 * @param param0 
 * @returns 
 */
export const SelectBoxV2 = forwardRef<any, SelectBoxProps>(
  (
    {
      formState,
      errors,
      name,
      isSearchBar = false,
      onInputChange,
      menuPlacement = "auto",
      normalPlaceholder,
      disabled,
      isSearchable,
      isClearable,
      error,
      minHeight = 52,
      height = 52,
      valueContainerMT = "9px",
      enableIndicatorsContainer = false,
      useStyledOption = false,
      isAutofilled = false,
      ...rest
    },
    ref
  ) => {
    const { submitCount } = formState;
    const styledTheme = useContext(ThemeContext);
    const { t } = useTranslation();
    const {
      placeholder,
      value,
      styles: customStyles,
      menuPortalTarget: menuPortalTargetProp,
      ...restProps
    } = rest;
    const normalizedValue = hasValue(value) ? value : null;
    const hasError = errors[name] || error;

    const defaultStyles = {
      control: (base: any) => ({
        ...base,
        ...(submitCount > 0 &&
          hasError && {
            border: `${borderPrimitiveTokens.border_2xl} solid ${styledTheme.errorColor}`,
            "&:hover": {
              border: `${borderPrimitiveTokens.border_2xl} solid ${styledTheme.errorColor}`,
            },
          }),
        height: height,
        minHeight: minHeight,
      }),
      dropdownIndicator: (base: any) => ({
        ...base,
        color: submitCount > 0 && hasError ? styledTheme.errorColor : "#808080",
      }),
      option: (base: any, state: any) => ({
        ...base,
        color: styledTheme.primaryTextColor,
        fontSize: styledTheme.fontSizes.medium,
        backgroundColor:
          /** Compare labels (since it's always unique)
           * Instead of using state.isSelected
           * There seems to be a bug with state.isSelected implementation, where multiple
           * options fulfill this condition, although one value is actually selected.
           * This should fix that.
           */
          state?.selectProps?.value?.label === state.label
            ? styledTheme.secondaryButtonBG
            : styledTheme.primaryBG,
        ":hover": {
          backgroundColor: styledTheme.secondaryButtonBG,
        },
      }),
      valueContainer: (provided: any, state: any) => ({
        ...provided,
        overflow: "visible",
        fontSize: styledTheme.fontSizes.medium,
        marginTop: state.isSelected ? "0px" : valueContainerMT,
      }),
      placeholder: (provided: any, state: any) => ({
        ...provided,
        position: normalPlaceholder ? "relative" : "absolute",
        top:
          hasValue(state.selectProps.value) || state.selectProps.inputValue
            ? 2
            : "40%",
        transition: "top 0.1s, font-size 0.1s",
        fontSize:
          hasValue(state.selectProps.value) || state.selectProps.inputValue
            ? styledTheme.fontSizes.xs
            : styledTheme.fontSizes.medium,
        marginBottom: 2,
        display:
          normalPlaceholder &&
          (hasValue(state.selectProps.value) || state.selectProps.inputValue)
            ? "none"
            : "block",
        color: submitCount > 0 && hasError ? styledTheme.errorColor : "#808080",
      }),
      ...(enableIndicatorsContainer && {
        indicatorsContainer: (base: any) => ({
          ...base,
          height: height,
        }),
      }),
    };

    // Merge custom styles with default styles
    const mergedStyles = customStyles
      ? Object.keys(defaultStyles).reduce((acc, key) => {
          const defaultStyleFn = (defaultStyles as any)[key];
          const customStyleFn = (customStyles as any)[key];

          if (customStyleFn) {
            // If custom style exists, merge it with default
            acc[key] = (base: any, state: any) => {
              const defaultResult = defaultStyleFn
                ? defaultStyleFn(base, state)
                : base;
              const customResult = customStyleFn(base, state);
              return { ...defaultResult, ...customResult };
            };
          } else {
            // Use default style if no custom style
            acc[key] = defaultStyleFn;
          }
          return acc;
        }, {} as any)
      : defaultStyles;

    // Add any custom styles that don't exist in defaults
    if (customStyles) {
      Object.keys(customStyles).forEach((key) => {
        if (!(defaultStyles as any)[key]) {
          (mergedStyles as any)[key] = (customStyles as any)[key];
        }
      });
    }
    return (
      <div style={{ position: "relative" }} data-testid={placeholder}>
        <Select
          ref={ref}
          className="basic-single"
          isSearchable={isSearchable}
          classNamePrefix="select"
          menuPlacement={menuPlacement}
          onInputChange={onInputChange}
          isDisabled={disabled}
          value={normalizedValue}
          isClearable={isClearable && normalizedValue !== null}
          filterOption={filterOption}
          components={{
            ValueContainer: CustomValueContainer,
            IndicatorSeparator: () => null,
            Menu: Menu,
            DropdownIndicator: getDropdownIndicator(isAutofilled, isSearchBar),
            Option: useStyledOption
              ? CustomOptionWithStyledParens
              : CustomOption,
            SingleValue: useStyledOption
              ? CustomSingleValueWithStyledParens
              : components.SingleValue,
          }}
          theme={(theme) => ({
            ...theme,
            borderRadius: 4,
            colors: {
              ...theme.colors,
              primary:
                submitCount > 0 && hasError
                  ? styledTheme.errorColor
                  : styledTheme.tertiaryBG,
            },
          })}
          styles={mergedStyles}
          placeholder={placeholder}
          menuPortalTarget={
            menuPortalTargetProp === null ? undefined : menuPortalTargetProp
          }
          {...restProps}
        />
        {showErrors({ errors, error, name, t })}
      </div>
    );
  }
);
